import { useState } from 'react';
import { Message } from '@/schemas/message-schema';
import { v4 as uuidv4 } from 'uuid';

interface UseChatSessionProps {
    onMessagesChange?: (message: Message) => void;
}

const START = "<<NEXT_QUESTIONS>>";
const END = "<</NEXT_QUESTIONS>>";

type NextQuestion = { id: string; question: string; };
type NextQuestions = { continue: NextQuestion[]; branch: NextQuestion[]; };

function computeStreamingDisplay(raw: string): string {
    // Primary: strip anything once the block begins
    const startIdx = raw.indexOf(START);
    if (startIdx !== -1) return raw.slice(0, startIdx).trimEnd();

    // Fallback: model forgot START but started the block anyway
    const fallbackMarkers = ["\n[continue]", "\n[branch]", "\n- "];
    const idxs = fallbackMarkers.map(m => raw.indexOf(m)).filter(i => i !== -1);
    if (idxs.length) return raw.slice(0, Math.min(...idxs)).trimEnd();

    return raw;
}

function extractNextQuestions(content: string): {
    cleanedContent: string;
    nextQuestions: NextQuestions;
} {
    const startIdx = content.lastIndexOf(START);
    if (startIdx === -1) {
        return { cleanedContent: content.trim(), nextQuestions: { continue: [], branch: [] } };
    }

    const endIdx = content.indexOf(END, startIdx);
    if (endIdx === -1) {
        return { cleanedContent: content.trim(), nextQuestions: { continue: [], branch: [] } };
    }

    const block = content.slice(startIdx + START.length, endIdx).trim();
    const cleanedContent = (content.slice(0, startIdx) + content.slice(endIdx + END.length)).trim();

    const lines = block.split("\n").map(l => l.trim()).filter(Boolean);

    const result: NextQuestions = { continue: [], branch: [] };
    let current: keyof NextQuestions | null = null;

    for (const line of lines) {
        if (line === "[continue]") { current = "continue"; continue; }
        if (line === "[branch]") { current = "branch"; continue; }
        if (!current) continue;

        const stripped = line.startsWith("- ") ? line.slice(2).trim() : line;
        const sep = stripped.indexOf("|");
        if (sep === -1) continue;

        const id = stripped.slice(0, sep).trim();
        const question = stripped.slice(sep + 1).trim();
        if (!id || !question) continue;

        result[current].push({ id, question });
    }

    return { cleanedContent, nextQuestions: result };
}


export function useChatSession({ onMessagesChange }: UseChatSessionProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);

    const sendMessage = async (content: string) => {
        if (!content.trim() || isLoading) return;

        const userMessage = content.trim();

        // Generate ID for user message
        const userMessageId = uuidv4();
        onMessagesChange?.({ id: userMessageId, role: 'user', content: userMessage });
        setIsLoading(true);

        try {
            const assistantMessageId = uuidv4();

            onMessagesChange?.({
                id: assistantMessageId,
                role: "assistant",
                content: "",
                isStreaming: true,
                // nextQuestions: { continue: [], branch: [] }, // optional placeholder
            });

            const response = await fetch("/api/chat", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(sessionId && { "X-Session-Id": sessionId })
                },
                body: JSON.stringify({
                    message: userMessage,
                    ...(sessionId && { sessionId }),
                }),
            });

            if (response.status === 429) {
                const data = await response.json();
                onMessagesChange?.({
                    id: assistantMessageId,
                    role: "assistant",
                    content: data.message || "Rate limit exceeded. Please try again later.",
                    isStreaming: false,
                });
                return
                // throw new Error(data.message || "Rate limit exceeded. Please try again later.");
            }

            if (!response.ok) throw new Error("Network response was not ok");

            const newSessionId = response.headers.get("X-Session-Id");
            if (newSessionId && !sessionId) setSessionId(newSessionId);

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            if (!reader) throw new Error("No reader available");

            let assistantMessage = ""; // raw buffer (includes NEXT_QUESTIONS block)
            let lastUpdateTime = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                assistantMessage += chunk;

                const now = Date.now();
                if (now - lastUpdateTime > 50) {
                    const display = computeStreamingDisplay(assistantMessage);

                    // IMPORTANT: we REPLACE content with `display` (derived from raw)
                    onMessagesChange?.({
                        id: assistantMessageId,
                        role: "assistant",
                        content: display,
                        isStreaming: true,
                    });

                    lastUpdateTime = now;
                }
            }

            // Final: parse out questions and update the message one last time
            const { cleanedContent, nextQuestions } = extractNextQuestions(assistantMessage);

            onMessagesChange?.({
                id: assistantMessageId,
                role: "assistant",
                content: cleanedContent,
                isStreaming: false,
                nextQuestions,
            });

        } catch (error) {
            console.error("Error sending message:", error);
            onMessagesChange?.({
                role: "assistant",
                content: error instanceof Error ? error.message : "Sorry, something went wrong. Please try again.",
                isStreaming: false,
            });
        } finally {
            setIsLoading(false);
        }

    };

    return {
        sendMessage,
        isLoading
    };
}
